import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NewsService } from '../../../core/services/news/news.service';
import { RouterLink } from '@angular/router';
import { LoadingComponent } from "../../../shared/loading/loading.component";

@Component({
  selector: 'app-news',
  standalone: true,
  imports: [CommonModule, RouterLink, LoadingComponent, FormsModule],
  templateUrl: './news.component.html',
  styleUrl: './news.component.scss'
})
export class NewsComponent implements OnInit {

  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalNews: number = 0;
  hasMore: boolean = false;
  lastKey: string | null = null;
  pageKeys: Map<number, string | null> = new Map();

  // News Data
  newsList: any[] = [];
  filteredNews: any[] = [];
  allNewsForCategories: any[] = []; // Store all news for category counting

  // Filter
  categories: string[] = [];
  selectedCategory: string = 'all';

  // Loading
  loading: boolean = true;
  loadingMore: boolean = false;
  errorMessage: string = '';

  constructor(private newsService: NewsService) {}

  ngOnInit(): void {
    this.initializeData();
  }

  private async initializeData(): Promise<void> {
    try {
      this.errorMessage = '';
      
      // Load first page immediately (fast)
      await this.loadNewsPage(1);
      
      // Load categories and count asynchronously (non-blocking)
      this.loadCategoriesAsync();
      this.loadTotalCountAsync();
      
      this.applyFilter();
    } catch (error) {
      console.error('Error initializing news:', error);
      this.errorMessage = 'حدث خطأ أثناء تحميل الأخبار. يرجى المحاولة مرة أخرى.';
      this.loading = false;
    }
  }

  private async loadCategoriesAsync(): Promise<void> {
    try {
      // Extract categories from loaded news first (instant)
      this.extractCategories();
      
      // Then load full category list in background (if needed)
      setTimeout(async () => {
        try {
          const allCategories = await this.newsService.getCategoriesOnly();
          if (allCategories.length > this.categories.length) {
            this.categories = allCategories;
          }
        } catch (error) {
          console.warn('Could not load full category list');
        }
      }, 100);
    } catch (error) {
      console.warn('Error loading categories:', error);
    }
  }

  private async loadTotalCountAsync(): Promise<void> {
    try {
      // Load count in background (non-blocking)
      setTimeout(async () => {
        try {
          this.totalNews = await this.newsService.getTotalNewsCount();
          this.totalPages = Math.ceil(this.totalNews / 10);
        } catch (error) {
          console.warn('Could not load total count');
        }
      }, 100);
    } catch (error) {
      console.warn('Error loading total count:', error);
    }
  }


  private async loadNewsPage(pageNumber: number): Promise<void> {
    if (pageNumber === 1) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }

    try {
      const lastKey = this.pageKeys.get(pageNumber - 1);
      const lastTimestamp = lastKey ? Number(lastKey) : null;
      const result = await this.newsService.getNewsPage(pageNumber, lastTimestamp);

      if (pageNumber === 1) {
        this.newsList = result.news;
        // Use cached data for categories if available
        this.allNewsForCategories = this.newsService.getCachedNewsForCategories() || result.news;
        this.pageKeys.clear();
        this.pageKeys.set(1, result.lastKey);
      } else {
        // Append to existing list for "load more" functionality
        this.newsList = [...this.newsList, ...result.news];
        // Update categories from cached data
        const cached = this.newsService.getCachedNewsForCategories();
        if (cached.length > 0) {
          this.allNewsForCategories = cached;
        } else {
          this.allNewsForCategories = [...this.allNewsForCategories, ...result.news];
        }
      }

      this.hasMore = result.hasMore;
      this.lastKey = result.lastKey;
      this.pageKeys.set(pageNumber, result.lastKey);

      this.applyFilter();
    } catch (error: any) {
      console.error('Error loading news page:', error);
      if (!this.errorMessage) {
        this.errorMessage = 'حدث خطأ أثناء تحميل الأخبار. يرجى المحاولة مرة أخرى.';
      }
    } finally {
      this.loading = false;
      this.loadingMore = false;
    }
  }

  private extractCategories(): void {
    // Get categories from all loaded news
    const allCategories = this.allNewsForCategories
      .map(item => item.category)
      .filter(category => category && category.trim() !== '');
    
    this.categories = [...new Set(allCategories)].sort();
  }

  onCategoryChange(): void {
    this.applyFilter();
  }

  private applyFilter(): void {
    if (this.selectedCategory === 'all') {
      this.filteredNews = [...this.newsList];
    } else {
      this.filteredNews = this.newsList.filter(
        item => item.category === this.selectedCategory
      );
    }
  }

  // ========== PAGINATION METHODS ==========
  async goToPage(pageNumber: number): Promise<void> {
    if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) {
      return;
    }

    // If going to a later page, we need to load sequentially
    if (pageNumber > this.currentPage) {
      await this.loadPagesSequentially(this.currentPage + 1, pageNumber);
    } else {
      // For going back, we can load directly if we have the key
      if (this.pageKeys.has(pageNumber - 1)) {
        this.currentPage = pageNumber;
        await this.loadNewsPage(pageNumber);
      } else {
        // Need to load from beginning
        await this.loadPagesSequentially(1, pageNumber);
      }
    }

    this.currentPage = pageNumber;
    this.applyFilter();
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private async loadPagesSequentially(startPage: number, endPage: number): Promise<void> {
    for (let page = startPage; page <= endPage; page++) {
      if (!this.pageKeys.has(page)) {
        await this.loadNewsPage(page);
      }
    }
  }

  async loadMoreNews(): Promise<void> {
    if (this.loadingMore || !this.hasMore) {
      return;
    }

    const nextPage = this.currentPage + 1;
    await this.loadNewsPage(nextPage);
    this.currentPage = nextPage;
  }

  get newsCount(): number {
    return this.filteredNews.length;
  }

  getCategoryCount(category: string): number {
    // Use cached data if available (faster)
    const cached = this.newsService.getCachedNewsForCategories();
    if (cached.length > 0) {
      return cached.filter(item => item.category === category).length;
    }
    // Fallback to loaded news
    return this.allNewsForCategories.filter(item => item.category === category).length;
  }

  // Load category count asynchronously (non-blocking)
  async getCategoryCountAsync(category: string): Promise<number> {
    try {
      return await this.newsService.getCategoryCount(category);
    } catch (error) {
      return this.getCategoryCount(category); // Fallback to sync method
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 5);
    
    if (this.totalPages <= 5) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + 4);
      
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  // Expose Math for template
  Math = Math;

}
